import { postCenterSignUp } from '@/api/sign-up/postCenterSignUp';
import { ATTACHED_FILE_TYPE } from '@/api/type/commonTypes';
import { PostCenterSignUpRequest } from '@/api/type/types';
import { countries } from '@/constants/countries';
import {
  FileTransferService,
  FileTransferSource,
  FileUploadSource,
} from '@/libs/r2-service-client';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import MemberInfoSection from '../all/MemberInfoSection';
import LoginInfoSection from '../all/LoginInfoSection';
import PersonalInfoSection from '../all/BasicPersonalInfoSection';
import CountrySelectField from '../all/CountrySelectField';
import PhoneNumberFiled from './CenterPhoneNumberFiled';
import AddressSection from '../all/AddressSection';
import { validatePassword } from '@/utils/regex';
import { fonts } from '@/theme/fonts';
import { useTranslation } from 'react-i18next';

const MAX_FILE_COUNT = 5;
const CenterSignUpPage = () => {
  const [formData, setFormData] = useState<PostCenterSignUpRequest>({
    loginId: '',
    password: '',
    firstName: '',
    lastName: '',
    birthDate: '',
    gender: 'MALE',
    countryCode: 'KR',
    contactCountryNumber: '',
    contactNumber: '',
    agreementId: 'AGR20240818',
    agreementVersion: '1.0',
    centerName: '',
    centerContactNumber: '',
    centerHeadName: '',
    centerAddress: {
      postalCode: '',
      address1: '',
      address2: '',
    },
    centerQualificationDocuments: [],
    remark: '',
  });
  const [passwordConfirm, setPasswordConfirm] = useState<string>('');
  const [passwordError, setPasswordError] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();

  // 국적 변경 시 국가코드 자동 매칭 로직
  useEffect(() => {
    const selectedCountry = countries.find(
      (country) => country.code === formData.countryCode
    );

    const phoneCode = selectedCountry?.phone ?? '';

    setFormData((prev) => ({
      ...prev,
      contactCountryNumber: phoneCode,
    }));
  }, [formData.countryCode]);

  const handleChange = useCallback(
    (
      e: React.ChangeEvent<
        HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
      >
    ) => {
      const { name, value, type } = e.target;

      if (type === 'checkbox') {
        const checked = (e.target as HTMLInputElement).checked;

        setFormData((prev) => ({
          ...prev,
          [name]: checked,
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          [name]: value,
        }));
      }
    },
    []
  );

  const handleAddressChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      setFormData((prev) => ({
        ...prev,
        centerAddress: {
          ...prev.centerAddress,
          [name]: value,
        },
      }));
    },
    []
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (!validatePassword(formData.password)) {
        alert(t('signUp.passwordRule'));
        return;
      }
      if (formData.password !== passwordConfirm) {
        alert(t('signUp.passwordMismatch'));
        return;
      }

      if (formData.centerQualificationDocuments.length === 0) {
        alert(t('centerSignUp.businessDocumentRequired'));
        return;
      }

      try {
        const res = await postCenterSignUp(formData);

        if (res.isSuccess) {
          alert(t('centerSignUp.registerSuccess', { name: res.data.loginId }));
        } else if (res.error.code === 'E_MEMBERSHIP_001') {
          alert(t('signUp.emailAlreadyExists'));
        }
      } catch (err) {
        alert(t('common.serverConnectionFailed'));
      }
    },
    [formData, passwordConfirm, t]
  );

  const selectedCountry = countries.find(
    (country) => country.code === formData.countryCode
  );

  const phoneCode = selectedCountry ? `+${selectedCountry.phone}` : '';

  const uploadFiles = useCallback(
    async (files: File[]) => {
      if (!files.length) return;

      // 파일 개수 체크
      if (files.length > MAX_FILE_COUNT) {
        alert(t('centerSignUp.maxFileCount'));
        return;
      }

      // 확장자 체크
      const allowedExtensions = ['jpg', 'jpeg', 'png', 'pdf'];

      const invalidExtFiles = files.filter((file) => {
        const ext = file.name.split('.').pop()?.trim().toLowerCase();

        return !ext || !allowedExtensions.includes(ext);
      });

      if (invalidExtFiles.length > 0) {
        alert(t('centerSignUp.supportedFileTypes'));
        return;
      }

      // 파일 크기 체크 (40MB)
      const maxSize = 40 * 1024 * 1024;
      const oversizedFiles = files.filter((file) => file.size > maxSize);

      if (oversizedFiles.length > 0) {
        alert(t('centerSignUp.maxFileSize'));
        return;
      }

      try {
        const uploadSource = new FileUploadSource('USER_UPLOAD');
        files.forEach((file) => uploadSource.addBrowserData(file));
        const transferSource = new FileTransferSource(uploadSource);
        const fileService = new FileTransferService();
        const uploadResult = await fileService.transfer(transferSource);

        if (!uploadResult.upload || uploadResult.upload.length === 0) return;
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        const attachedFiles: ATTACHED_FILE_TYPE[] = uploadResult.upload.map(
          (file, index) => {
            const ext = file.fileName.split('.').pop()?.trim().toLowerCase();

            return {
              id: file.fileId ?? '',
              name: file.fileName,
              size: file.fileSize ?? 0,
              type: ext ?? '',
              index,
            };
          }
        );
        setFormData((prev) => ({
          ...prev,
          centerQualificationDocuments: [
            ...prev.centerQualificationDocuments,
            ...attachedFiles,
          ],
        }));
      } catch (error) {
        alert(t('centerSignUp.fileUploadFailed'));
        console.error(error);
      }
    },
    [t]
  );

  const onFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { files } = e.target;
      if (files && files.length > 0) {
        if (e.target.files) {
          uploadFiles(Array.from(e.target.files));
        }
      }
    },
    [uploadFiles]
  );

  const handleRemove = (fileId: string) => {
    setFormData((prev) => ({
      ...prev,
      centerQualificationDocuments: prev.centerQualificationDocuments.filter(
        (file) => file.id !== fileId
      ),
    }));
  };

  return (
    <Container>
      <MemberInfoSection role="CENTER" />
      <form onSubmit={handleSubmit}>
        <LoginInfoSection
          loginId={formData.loginId}
          password={formData.password}
          passwordConfirm={passwordConfirm}
          passwordError={passwordError}
          onChange={handleChange}
          onPasswordConfirmChange={(value) => {
            setPasswordConfirm(value);

            if (formData.password && formData.password !== value) {
              setPasswordError(t('signUp.passwordMismatch'));
            } else {
              setPasswordError('');
            }
          }}
        />
        <PersonalInfoSection formData={formData} onChange={handleChange} />

        {/* 연락처 및 소속 정보 */}
        <Section>
          <SectionTitle>
            {t('centerSignUp.contactAndCenterInformation')}
          </SectionTitle>
          <GridContainer>
            <CountrySelectField
              countryCode={formData.countryCode}
              onChange={handleChange}
            />
            <PhoneNumberFiled
              phoneCode={phoneCode}
              formData={formData}
              onChange={handleChange}
            />
            <FormGroup>
              <Label $required>{t('common.representativeName')}</Label>
              <Input
                type="text"
                name="centerHeadName"
                value={formData.centerHeadName}
                onChange={handleChange}
                placeholder={t('centerSignUp.representativeNamePlaceholder')}
                required
              />
            </FormGroup>
            <AddressSection
              countryCode={formData.countryCode}
              address={formData.centerAddress}
              onChange={handleAddressChange}
              role="CENTER"
            />
            {/* 파일 업로드 */}
            <FormGroup>
              <Label $required>{t('centerSignUp.businessDocument')}</Label>
              <FileUploadBox>
                <input
                  type="file"
                  ref={fileInputRef}
                  multiple
                  onChange={onFileChange}
                  style={{ display: 'none' }}
                />
                <UploadButton
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                >
                  {t('common.selectFile')}
                </UploadButton>

                <FileList>
                  {formData.centerQualificationDocuments.map((file) => (
                    <FileItem key={file.id}>
                      <FileInfo>
                        <FileName>{file.name}</FileName>
                        <FileSize>{(file.size / 1024).toFixed(1)}KB</FileSize>
                      </FileInfo>

                      <DeleteButton
                        type="button"
                        onClick={() => handleRemove(file.id)}
                      >
                        {t('common.delete')}
                      </DeleteButton>
                    </FileItem>
                  ))}
                </FileList>
              </FileUploadBox>
            </FormGroup>
            <FormGroup>
              <Label $required>{t('signUp.registrationReason')}</Label>
              <TextArea
                name="remark"
                value={formData.remark}
                onChange={handleChange}
                placeholder={t('signUp.registrationReasonPlaceholder')}
                rows={4}
                required
              />
            </FormGroup>
          </GridContainer>
        </Section>
        <ButtonSection>
          <Button type="submit">{t('centerSignUp.createCenterAccount')}</Button>
        </ButtonSection>
      </form>
    </Container>
  );
};

export default CenterSignUpPage;

const Container = styled.div`
  width: 100%;
  max-width: 1000px;
  padding: 24px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
`;

const Section = styled.section`
  padding: 20px 0;
  border-bottom: 1px solid #e5e7eb;
`;

const SectionTitle = styled.h3`
  margin-bottom: 16px;
  font-size: 15px;
  font-weight: ${fonts.weight.semiBold};
  color: #374151;
  margin-bottom: 16px;
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

interface LabelProps {
  $required?: boolean;
}

const Label = styled.label<LabelProps>`
  font-size: 13px;
  font-weight: 600;
  color: #4a4a4a;

  &::after {
    content: ${(props) => (props.$required ? "' *'" : "''")};
    color: #ff4d4f;
  }
`;

interface InputProps {
  $error?: boolean;
}

const Input = styled.input<InputProps>`
  height: 38px;
  padding: 0 12px;
  font-size: 14px;
  color: #374151;
  border: 1px solid ${({ $error }) => ($error ? '#ef4444' : '#d1d5db')};
  border-radius: 6px;
  outline: none;
  &:focus {
    border-color: #2563eb;
  }
  &::placeholder {
    color: #9ca3af;
  }
`;

const ButtonSection = styled.div`
  display: flex;
  justify-content: end;
  padding-top: 24px;
`;

const Button = styled.button`
  padding: 0 15px;
  height: 40px;
  border-radius: 6px;
  background: #2563eb;
  color: #ffffff;
  font-size: 14px;
  font-weight: ${fonts.weight.semiBold};
  &:hover {
    background: #1d4ed8;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  line-height: 1.5;
  resize: vertical;
  box-sizing: border-box;
  outline: none;
  resize: none;

  &:focus {
    border-color: #2563eb;
  }

  &::placeholder {
    color: #bfbfbf;
  }
`;

const FileUploadBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const UploadButton = styled.button`
  height: 40px;
  padding: 0px 16px;
  font-size: 14px;
  font-weight: ${fonts.weight.semiBold};
  width: fit-content;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: 0.2s;
  background: #ffffff;
  border: 1px solid #d1d5db;
  color: #374151;

  &:hover {
    background: #f9fafb;
  }
`;

const FileList = styled.ul`
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FileItem = styled.li`
  display: flex;
  justify-content: space-between;
  align-items: center;

  padding: 10px 12px;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
`;

const FileInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const FileName = styled.span`
  font-size: 14px;
  color: #374151;
  font-weight: 500;
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const FileSize = styled.span`
  font-size: 12px;
  color: #6b7280;
`;

const DeleteButton = styled.button`
  font-size: 12px;
  color: #ff4d4f;
  background: transparent;
  border: none;
  cursor: pointer;

  &:hover {
    color: #dc2626;
  }
`;
